import {
    createTheme,
    CSSVariablesResolver,
    type DefaultMantineColor,
    type MantineColorsTuple,
    NumberInput,
} from '@mantine/core'
import type {DatesProviderSettings} from '@mantine/dates'

import {SEMANTIC_CSS_VARIABLES} from './theme/semantic-colors'

export const datesSettings: DatesProviderSettings = {consistentWeeks: true, firstDayOfWeek: 0}

// Navy scale: shade 6 #12294D, shade 7 #00204A
const brand: MantineColorsTuple = [
    '#edf0f6',
    '#d6dce8',
    '#aab7cd',
    '#7b8fb0',
    '#546c97',
    '#2f4a75',
    '#12294d',
    '#00204a',
    '#001a3d',
    '#001331',
]

// Orange scale: shade 6 #E7751C
const highlight: MantineColorsTuple = [
    '#fdf2e8',
    '#fbe0c8',
    '#f6c192',
    '#f1a25c',
    '#ed8b38',
    '#e97f28',
    '#e7751c',
    '#c96214',
    '#a8510f',
    '#87410b',
]

type ExtendedCustomColors = 'brand' | 'highlight' | DefaultMantineColor

declare module '@mantine/core' {
    export interface MantineThemeColorsOverride {
        colors: Record<ExtendedCustomColors, MantineColorsTuple>
    }
}

/** Extra space between the value and stepper controls (helps right-aligned amounts). */
const NUMBER_INPUT_CONTROLS_GAP = '0.375rem'

const BRAND_FONT_BODY = "'Work Sans', system-ui, -apple-system, 'Segoe UI', sans-serif"
const BRAND_FONT_DISPLAY = "'Big Shoulders', 'Arial Narrow', system-ui, sans-serif"
const BRAND_FONT_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

export const theme = createTheme({
    colors: {brand, highlight},
    primaryColor: 'brand',
    primaryShade: 6,
    black: '#12294d',
    fontFamily: BRAND_FONT_BODY,
    fontFamilyMonospace: BRAND_FONT_MONO,
    headings: {
        fontFamily: BRAND_FONT_DISPLAY,
        fontWeight: '700',
        sizes: {
            h1: {fontSize: '2.5rem', lineHeight: '1.1'},
            h2: {fontSize: '2rem', lineHeight: '1.15'},
            h3: {fontSize: '1.625rem', lineHeight: '1.2'},
            h4: {fontSize: '1.375rem', lineHeight: '1.25'},
        },
    },
    components: {
        NumberInput: NumberInput.extend({
            vars: () => ({
                root: {
                    '--ni-right-section-width-xs': `calc(1.0625rem * var(--mantine-scale) + ${NUMBER_INPUT_CONTROLS_GAP})`,
                    '--ni-right-section-width-sm': `calc(1.5rem * var(--mantine-scale) + ${NUMBER_INPUT_CONTROLS_GAP})`,
                    '--ni-right-section-width-md': `calc(1.6875rem * var(--mantine-scale) + ${NUMBER_INPUT_CONTROLS_GAP})`,
                    '--ni-right-section-width-lg': `calc(1.9375rem * var(--mantine-scale) + ${NUMBER_INPUT_CONTROLS_GAP})`,
                    '--ni-right-section-width-xl': `calc(2.125rem * var(--mantine-scale) + ${NUMBER_INPUT_CONTROLS_GAP})`,
                },
                controls: {},
            }),
        }),
    },
})

export const cssVariablesResolver: CSSVariablesResolver = () => ({
    variables: {
        ...SEMANTIC_CSS_VARIABLES,
    },
    light: {},
    dark: {},
})
